/** @jsx jsx */
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { jsx, Flex, Container } from "theme-ui";

interface HeroSectionProps {
  fullSizeImage?: boolean;
  smallerContent?: boolean;
  headline?: string | JSX.Element;
  preHeadline?: string | JSX.Element;
  pText?: string | JSX.Element;
  cta?: JSX.Element;
  buttonText?: string;
  buttonLink?: string;
  backgroundColor?: string;
  headingColor?: string;
  pTextColor?: string;
  smallerHeading?: boolean;
  mobileAlignment?: any;
  desktopImage?: IGatsbyImageData;
  mobileImage?: IGatsbyImageData;
  desktopImageAlt?: string;
  mobileImageAlt?: string;
}

const HeroSection = ({
  fullSizeImage,
  smallerContent,
  headline,
  preHeadline,
  pText,
  cta,
  buttonText,
  buttonLink,
  backgroundColor,
  headingColor,
  pTextColor,
  smallerHeading,
  mobileAlignment,
  desktopImage,
  mobileImage,
  desktopImageAlt,
  mobileImageAlt,
}: HeroSectionProps) => {
  return (
    <section
      sx={{
        backgroundColor: backgroundColor,
        position: "relative",
        display: "flex", // Flexbox for centering
        flexDirection: "column",
        justifyContent: "center", // Vertically center content
        pt: "40px", // Padding only on top
        pb: "0px", // Remove bottom padding to make image flush
      }}
    >
      <Container
        sx={{
          maxWidth: fullSizeImage ? "1300px" : "1024px",
          display: "flex",
          alignItems: "center", // Vertically align items
          justifyContent: "space-between", // Space between image and content
          flexWrap: "wrap", // Allow content and image to wrap on small screens
          pb: "0px", // Ensure the container doesn't push the bottom
        }}
      >
        {desktopImage && (
          <div
            sx={{
              flex: "1 1 auto",
              display: ["none", null, "flex"], // Hide on mobile, show on larger screens
              justifyContent: "flex-end",
              maxWidth: ["100%", null, "50%"],
              pb: "0px", // Make sure there's no bottom padding
            }}
          >
            <GatsbyImage
              image={desktopImage}
              alt={desktopImageAlt}
              sx={{
                width: "100%",
                height: "auto", // Maintain aspect ratio
                objectFit: "contain",
                mb: "0px", // Ensure the image is flush at the bottom
              }}
            />
          </div>
        )}

        <Flex
          sx={{
            flex: "1 1 auto",
            flexDirection: "column",
            justifyContent: "center", // Center content vertically
            alignItems: ["center", null, "flex-start"], // Align text based on screen size
            textAlign: [mobileAlignment, null, "left"],
            maxWidth: ["100%", null, "50%"],
            px: [3, null, 4],
            mt: [4, null, 0], // Add some margin to prevent overlap
          }}
        >
          <h4 sx={{ variant: "text.preHeading" }}>{preHeadline}</h4>
          <h1
            sx={{
              fontSize: smallerHeading ? ["24px", "34px", "48px"] : ["34px", "48px", "72px"],
              color: headingColor,
              mb: 3,
            }}
          >
            {headline}
          </h1>
          {pText && <p sx={{ color: pTextColor, fontSize: 2, mb: 4 }}>{pText}</p>}
          {buttonText && buttonLink && !cta && (
            <a
              sx={{
                variant: "buttons.primary",
                width: "260px",
                textAlign: "center",
              }}
              href={buttonLink}
              target="_blank"
            >
              {buttonText}
            </a>
          )}
          {cta && cta}
        </Flex>
      </Container>

      {mobileImage && (
        <div
          sx={{
            display: ["block", null, "none"], // Show mobile image only on small screens
            width: "100%",
            mt: 3,
            textAlign: "center", // Center the container element
          }}
        >
          <GatsbyImage
            image={mobileImage}
            alt={mobileImageAlt}
            sx={{ 
              width: "100%", 
              maxWidth: "275px", // Max width for the image
              height: "auto", 
              objectFit: "contain",
              margin: "0 auto" // Center the image
            }}
          />
        </div>
      )}
    </section>
  );
};

export default HeroSection;
